import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { SeedTree, type RandomStream, type Seed } from '../../mod-035/src/index.js';

export const NOISE_COLORS = Object.freeze(['white', 'pink', 'brown', 'blue'] as const);
export type NoiseColor = (typeof NOISE_COLORS)[number];

export interface NoiseGeneratorOptions {
  readonly sampleRate: number;
  readonly seed?: Seed;
  readonly color?: NoiseColor;
  readonly stereoCorrelation?: number;
}

const UINT32_MAX = 0xffff_ffff;
const COLOR_BY_INDEX: readonly NoiseColor[] = NOISE_COLORS;
const OUTPUT_SCALE = 0.4;

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

function assertColor(color: string): asserts color is NoiseColor {
  if (!(NOISE_COLORS as readonly string[]).includes(color)) throw new RangeError(`unsupported noise color: ${color}`);
}

const clampUnit = (value: number): number => Math.max(-1, Math.min(1, value));

/** Stateful color filters driven by a uniform bipolar excitation. */
class ColorFilter {
  private pink0 = 0;
  private pink1 = 0;
  private pink2 = 0;
  private pink3 = 0;
  private pink4 = 0;
  private pink5 = 0;
  private pink6 = 0;
  private brown = 0;
  private previousPink = 0;

  reset(): void {
    this.pink0 = 0;
    this.pink1 = 0;
    this.pink2 = 0;
    this.pink3 = 0;
    this.pink4 = 0;
    this.pink5 = 0;
    this.pink6 = 0;
    this.brown = 0;
    this.previousPink = 0;
  }

  next(color: NoiseColor, white: number): number {
    if (color === 'white') return white;
    if (color === 'pink') return this.nextPink(white);
    if (color === 'brown') {
      // Leaky integration prevents runaway drift while retaining a -6 dB/octave region.
      this.brown = (this.brown + 0.02 * white) / 1.02;
      return clampUnit(this.brown * 3.5);
    }
    // Differentiating pink (-3 dB/octave) produces blue (+3 dB/octave).
    const pink = this.nextPink(white);
    const output = (pink - this.previousPink) * 0.5;
    this.previousPink = pink;
    return output;
  }

  /** Paul Kellet's bounded seven-pole approximation, about -3 dB/octave. */
  private nextPink(white: number): number {
    this.pink0 = 0.99886 * this.pink0 + white * 0.0555179;
    this.pink1 = 0.99332 * this.pink1 + white * 0.0750759;
    this.pink2 = 0.969 * this.pink2 + white * 0.153852;
    this.pink3 = 0.8665 * this.pink3 + white * 0.3104856;
    this.pink4 = 0.55 * this.pink4 + white * 0.5329522;
    this.pink5 = -0.7616 * this.pink5 - white * 0.016898;
    const output = this.pink0 + this.pink1 + this.pink2 + this.pink3 + this.pink4 + this.pink5 + this.pink6 + white * 0.5362;
    this.pink6 = white * 0.115926;
    return clampUnit(output * 0.11);
  }
}

class DcBlocker {
  private readonly coefficient: number;
  private previousInput = 0;
  private previousOutput = 0;

  constructor(sampleRate: number) {
    this.coefficient = Math.exp(-2 * Math.PI * 5 / sampleRate);
  }

  reset(): void {
    this.previousInput = 0;
    this.previousOutput = 0;
  }

  process(input: number): number {
    const output = input - this.previousInput + this.coefficient * this.previousOutput;
    this.previousInput = input;
    this.previousOutput = output;
    return output;
  }
}

/**
 * Seeded stereo noise source. It owns only DSP state and writes to caller-owned
 * buffers; graph scheduling, UI and buffer allocation remain host concerns.
 */
export class NoiseGeneratorBank {
  readonly sampleRate: number;

  private seed: Seed;
  private color: NoiseColor;
  private stereoCorrelation: number;
  private leftStream!: RandomStream;
  private independentStream!: RandomStream;
  private readonly leftColor = new ColorFilter();
  private readonly independentColor = new ColorFilter();
  private readonly leftDc: DcBlocker;
  private readonly rightDc: DcBlocker;

  constructor(options: NoiseGeneratorOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    const color = options.color ?? 'white';
    assertColor(color);
    const stereoCorrelation = options.stereoCorrelation ?? 1;
    assertRange('stereoCorrelation', stereoCorrelation, -1, 1);
    this.sampleRate = options.sampleRate;
    this.seed = options.seed ?? 0;
    this.color = color;
    this.stereoCorrelation = stereoCorrelation;
    this.leftDc = new DcBlocker(this.sampleRate);
    this.rightDc = new DcBlocker(this.sampleRate);
    this.reseed();
  }

  reset(): void {
    this.leftStream.reset();
    this.independentStream.reset();
    this.resetFilters();
  }

  setParameter(id: string, value: number): void {
    if (id === 'seed') {
      if (!Number.isInteger(value) || value < 0 || value > UINT32_MAX) throw new RangeError('seed must be a uint32 integer');
      this.seed = value;
      this.reseed();
      return;
    }
    if (id === 'color') {
      if (!Number.isInteger(value) || value < 0 || value >= COLOR_BY_INDEX.length) throw new RangeError('color must be an integer in [0, 3]');
      this.color = COLOR_BY_INDEX[value]!;
      this.resetFilters();
      return;
    }
    if (id === 'stereo-correlation') {
      assertRange(id, value, -1, 1);
      this.stereoCorrelation = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(left: Float32Array, right: Float32Array, frames = left.length): void {
    if (!Number.isInteger(frames) || frames < 0 || left.length < frames || right.length < frames) {
      throw new RangeError('output buffers are too short for frames');
    }
    if (arguments.length < 3 && left.length !== right.length) throw new RangeError('output buffers must have equal length');
    const independentGain = Math.sqrt(1 - this.stereoCorrelation * this.stereoCorrelation);
    for (let index = 0; index < frames; index += 1) {
      const leftRaw = this.leftColor.next(this.color, this.leftStream.uniform(-1, 1));
      const independentRaw = this.independentColor.next(this.color, this.independentStream.uniform(-1, 1));
      const rightRaw = this.stereoCorrelation * leftRaw + independentGain * independentRaw;
      left[index] = clampUnit(this.leftDc.process(leftRaw * OUTPUT_SCALE));
      right[index] = clampUnit(this.rightDc.process(rightRaw * OUTPUT_SCALE));
    }
  }

  private reseed(): void {
    const tree = new SeedTree(this.seed).child('mod-004');
    this.leftStream = tree.stream('left');
    this.independentStream = tree.stream('independent');
    this.resetFilters();
  }

  private resetFilters(): void {
    this.leftColor.reset();
    this.independentColor.reset();
    this.leftDc.reset();
    this.rightDc.reset();
  }
}

class NoiseModuleInstance implements DspModuleInstance {
  private readonly generator: NoiseGeneratorBank;
  private readonly maxBlockSize: number;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
    this.generator = new NoiseGeneratorBank({ sampleRate });
  }

  reset(): void { this.generator.reset(); }
  setParameter(id: string, value: number): void { this.generator.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const left = block.outputs.left;
    const right = block.outputs.right;
    if (!left || left.length < block.frames) throw new RangeError('left output buffer is missing or too short');
    if (!right || right.length < block.frames) throw new RangeError('right output buffer is missing or too short');
    this.generator.processInto(left, right, block.frames);
  }
}

export const mod004 = defineDspModule({
  descriptor: {
    id: 'mir.mod-004',
    name: 'Noise generator bank',
    version: '0.1.0',
    inputs: [],
    outputs: [
      { id: 'left', kind: 'audio', channels: 1 },
      { id: 'right', kind: 'audio', channels: 1 },
    ],
    parameters: [
      { id: 'seed', defaultValue: 0, minValue: 0, maxValue: UINT32_MAX, unit: 'uint32' },
      { id: 'color', defaultValue: 0, minValue: 0, maxValue: 3, unit: 'enum' },
      { id: 'stereo-correlation', defaultValue: 1, minValue: -1, maxValue: 1, unit: 'ratio' },
    ],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isFinite(context.sampleRate)
      || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new NoiseModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
